import { Checkbox } from '@/libs/components/src/lib/form/Checkbox';
import { Textarea } from '@/libs/components/src/lib/form/Textarea';
import {
  PlanWizardCategoryEnum,
  SOAP_PREFERENCE_SETTING_NAMES,
} from '@/libs/core/src/lib/constants/SOAPPreferences';
import { treatmentSubCategories } from '@/libs/core/src/lib/constants/treatmentKeys';
import { Condition } from '@chiroup/core/types/Condition.type';
import { EvaluationListItem } from '@chiroup/core/types/Evaluation.type';
import { Patient } from '@chiroup/core/types/Patient.type';
import {
  CarePlanComplaint,
  ComplaintStatuses,
  ImprovementOptions,
  ObjectiveParts,
  PlanWizardCategory,
  SOAPTemplates,
  Visit,
} from '@chiroup/core/types/PatientVisit.type';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { MeContext } from '../../../../../contexts/me.context';
import useConditionDetails from '../../../../../hooks/useConditionDetails';
import useEvaluations from '../../../../../hooks/useEvaluations';
import Modal from '../../../../common/Modal';
import useSettings from '../../../../report/hooks/useSettings';
import { useOptionalCollaborativeEncounter } from '../../collaborative-v2/contexts/collaborativeEncounter.context';
import { useEchoGuardedLocalNote } from '../../collaborative-v2/hooks/useEchoGuardedLocalNote';
import Improvement from './Improvement';
import Joints from './Joints';
import {
  calculateCmtExtraspinal,
  cmtServiceCodes,
  getCopyOfDetail,
  getNumberOfSpineRegionsAndDetailNotes,
} from './ObjectiveStep';
import SOAPChangesWarning from './SOAPChangesWarning';
import UnifiedMyofascial from './UnifiedMyofascial';
import UnifiedNeurological from './UnifiedNeurological';
import UnifiedOrthopedic from './UnifiedOrthopedic';
import UnifiedRangeOfMotion from './UnifiedRangeOfMotion';

// Helper functions for plan wizard category validation
const findTreatmentCategory = (planWizard: PlanWizardCategory[] | undefined) => {
  return planWizard?.find(category => category.name === PlanWizardCategoryEnum.Treatments);
};

const hasSelection = (
  category: PlanWizardCategory | undefined,
  predicate: (selection: any) => boolean
) => !!(category?.selections?.length && category.selections.find(predicate));

export const existsTreatmentSelection = (
  planWizardCategory: PlanWizardCategory[] | undefined,
  predicate: (selection: any) => boolean
) => {
  const treatmentCategory = findTreatmentCategory(planWizardCategory);

  return !planWizardCategory || hasSelection(treatmentCategory, predicate);
};

interface Props {
  setVisitForm: React.Dispatch<React.SetStateAction<Visit>>;
  signed: boolean;
  complaints: CarePlanComplaint[];
  visitForm: Visit;
  isSaving: boolean;
  patient: Patient;
  readOnly?: boolean;
  /** Which section panel starts open (desktop). Defaults to Joints. */
  initialPartOpen?: ObjectiveParts;
  /** Return true to swallow a section-open request (trial SOAP sandbox shows
   *  an informational pop-up for Neurologic instead of the interface).
   *  `surface` says which affordance was clicked: the desktop label column or
   *  the mobile accordion — the sandbox syncs its walkthrough beat on desktop
   *  clicks only. */
  interceptPartOpen?: (part: ObjectiveParts, surface?: 'mobile' | 'desktop') => boolean;
  /** Hides Remove region + Leg Length Inequality (trial SOAP sandbox). */
  hideRegionControls?: boolean;
  /** Rendered beneath the section-label column on desktop (trial SOAP sandbox
   *  walkthrough panel — an overlay there covers the spine picker). */
  desktopAside?: React.ReactNode;
  /** Below xl, hide the section-label accordion and render only the active
   *  (initialPartOpen) section's pane inline — the trial SOAP sandbox's mobile
   *  walkthrough is linear, module by module, with no master page (tester
   *  note #30). Desktop keeps the label column and free jumping. */
  soloPartBelowXl?: boolean;
  /** Hides the Notes Carry Forward checkbox (trial SOAP sandbox). */
  hideCarryForward?: boolean;
  /** Skips the spine designations/contraindications gate (trial SOAP sandbox):
   *  those queries need a real patient and the EHR entitlement, which the
   *  sandbox's synthetic visit lacks. INERT ON THIS BRANCH — the gate it
   *  bypasses ships with the spine designations feature
   *  (feature/spine-picker-enhancements); declared here so the sandbox can
   *  pass it, and the implementation joins up where both features exist. */
  disableSpineDesignations?: boolean;
  /** Forwarded to Orthopedic tests' "+" details control (trial SOAP sandbox). */
  interceptOrthoDetails?: () => void;
  /** Shows an informational pop-up instead of the add-muscles autocomplete
   *  (trial SOAP sandbox). */
  interceptAddMuscles?: () => void;
  /** Shows an informational pop-up instead of the Add Evaluations selects
   *  (trial SOAP sandbox). */
  interceptAddTests?: () => void;
  /** Shows an informational pop-up instead of switching Joints / Range of
   *  Motion regions, and collapses their mobile region selects to a compact
   *  "Region: <name>" chip (trial SOAP sandbox). */
  interceptRegionSwitch?: (part: ObjectiveParts) => void;
}

// Type for legacy format note structure
interface LegacyFormatNote {
  [complaintId: string]: {
    objective: {
      Joints: {
        detail: Record<string, Record<string, string>>;
      };
    };
  };
}

const UnifiedObjectiveStep: React.FC<Props> = ({
  visitForm,
  setVisitForm,
  signed,
  complaints,
  isSaving,
  patient: _patient,
  readOnly,
  initialPartOpen,
  interceptPartOpen,
  hideRegionControls,
  hideCarryForward,
  interceptOrthoDetails,
  interceptAddMuscles,
  interceptAddTests,
  interceptRegionSwitch,
  desktopAside,
  soloPartBelowXl,
}) => {
  const { me } = useContext(MeContext);
  const [rangeOfMotionIsOpen, setRangeOfMotionIsOpen] = useState(false);
  const [jointsIsOpen, setJointsIsOpen] = useState(false);
  const [myofascialIsOpen, setMyofascialIsOpen] = useState(false);
  const [neurologicalIsOpen, setNeurologicalIsOpen] = useState(false);
  const [orthopedicIsOpen, setOrthopedicIsOpen] = useState(false);
  const [partOpen, setPartOpen] = useState<ObjectiveParts | null>(
    initialPartOpen ?? ObjectiveParts.Joints
  );
  // Null outside a collaborative encounter (trial SOAP sandbox)
  const collaborative = useOptionalCollaborativeEncounter();

  // Get conditions from ACTIVE complaints only for unified objective
  const allComplaintConditions = useMemo(() => {
    const conditions = new Set<number>();
    complaints.forEach(complaint => {
      // Only include conditions from active complaints
      if (complaint.status === ComplaintStatuses.ACTIVE) {
        complaint?.assessment?.conditions?.forEach(c => {
          conditions.add(c.conditionId);
        });
      }
    });

    return Array.from(conditions);
  }, [complaints]);

  const { data: conditionsRetrieved, isFetching } = useConditionDetails({
    ids: allComplaintConditions,
  });

  // Get regions from ACTIVE complaints only
  // Get active complaint condition IDs - shared logic (as sorted array for stable identity)
  const activeComplaintConditionIds = useMemo(() => {
    if (!complaints?.length) return [];

    const conditionIds = new Set<number>();
    complaints.forEach(complaint => {
      if (complaint.status === ComplaintStatuses.ACTIVE) {
        complaint?.assessment?.conditions?.forEach(c => {
          conditionIds.add(c.conditionId);
        });
      }
    });

    return Array.from(conditionIds).sort((a, b) => a - b);
  }, [complaints]);

  // Get active complaint conditions data
  const activeComplaintConditions = useMemo(() => {
    const conditionsArray = conditionsRetrieved?.data || [];
    if (conditionsArray.length === 0 || activeComplaintConditionIds.length === 0) return [];

    const idSet = new Set(activeComplaintConditionIds);

    return conditionsArray.filter(
      (condition: Partial<Condition>) => condition.ID && idSet.has(condition.ID)
    );
  }, [conditionsRetrieved?.data, activeComplaintConditionIds]);

  // Create filtered conditions object containing only active complaint conditions
  const filteredActiveComplaintConditions = useMemo(() => {
    return activeComplaintConditions.reduce(
      (acc: { [key: number]: Partial<Condition> }, condition: Partial<Condition>) => {
        if (condition.ID) {
          acc[condition.ID] = condition;
        }

        return acc;
      },
      {} as Record<number, Partial<Condition>>
    );
  }, [activeComplaintConditions]);

  // Get all evaluations to check regions for filtering
  const { data: allEvaluations } = useEvaluations({
    hide: true,
    refetchOnMount: false,
  });

  const filteredOrthopedicTests = useMemo(() => {
    const existingTests = Object.keys(visitForm.unifiedObjective?.Orthopedic?.detail || {});

    if (!allEvaluations?.data) {
      return existingTests; // Fallback to existing tests if no evaluation data
    }

    const allAvailableTests = allEvaluations.data.map((evaluation: EvaluationListItem) =>
      String(evaluation.ID)
    );

    const combinedTests = [...new Set([...existingTests, ...allAvailableTests])];

    return combinedTests;
  }, [visitForm.unifiedObjective?.Orthopedic?.detail, allEvaluations?.data]);

  const { data: soapSettings } = useSettings({
    type: SOAP_PREFERENCE_SETTING_NAMES.SOAP_PREFERENCES,
  });
  const romSliderStep = useMemo(() => {
    const romSlider = soapSettings?.find(
      (item: any) =>
        item?.type === SOAP_PREFERENCE_SETTING_NAMES.SOAP_PREFERENCES &&
        item?.name === SOAP_PREFERENCE_SETTING_NAMES.OBJECTIVE_ROM
    )?.jsonValue;

    return romSlider ? Number(romSlider) : 5;
  }, [soapSettings]);

  // Stable callback for updating visitForm
  const updateVisitForm = useCallback(
    (val: string) => {
      setVisitForm(prev => ({
        ...prev,
        unifiedObjective: {
          ...(prev.unifiedObjective || {}),
          extraNotes: val,
        },
        soapCustomEditWarning: {
          ...prev?.soapCustomEditWarning,
          objective: false,
        },
      }));
    },
    [setVisitForm]
  );

  // Echo-guarded: a visitForm update carrying the notes as of the last
  // write (older than what was typed since) must not overwrite the
  // textarea, or keystrokes are lost and the cursor jumps to the end.
  // One note per encounter, so no complaint switch.
  const { localNote: localTextareaValue, editLocalNote } = useEchoGuardedLocalNote(
    collaborative
      ? {
          incoming: visitForm.unifiedObjective?.extraNotes || '',
          flushPendingWrites: collaborative.flushPendingWrites,
          // Shared debounced path: flushed on blur, page hide, unmount and
          // sign. Merges into live unifiedObjective at flush time.
          write: val =>
            collaborative.updateFieldDebounced(
              'unifiedObjective',
              () => ({
                ...(collaborative.getField('unifiedObjective') ?? visitForm.unifiedObjective ?? {}),
                extraNotes: val,
              }),
              'unifiedObjective:extraNotes'
            ),
          onLocalEdit: [
            () => {
              // Clear the warning immediately (NOT debounced): a debounced
              // clear can flush AFTER a collaborator's edit arms the flag and
              // unprotect their SOAP text. Skipped once cleared.
              const liveWarning = (collaborative.getField('soapCustomEditWarning') ||
                visitForm.soapCustomEditWarning ||
                {}) as Record<string, any>;

              if (liveWarning.objective !== false) {
                collaborative.updateField('soapCustomEditWarning', {
                  ...liveWarning,
                  objective: false,
                });
              }
            },
          ],
        }
      : {
          incoming: visitForm.unifiedObjective?.extraNotes || '',
          write: updateVisitForm,
        }
  );

  const onChangeDetail = useCallback(
    (part: ObjectiveParts) => (region: string, prop: string, val: object | string) => {
      if (signed || readOnly) return;
      setVisitForm(prev => {
        const detail = prev.unifiedObjective?.[part]?.detail || {};
        let processedVal: object | string | undefined = val;

        if (part === 'Joints' && typeof val === 'string' && region !== 'SPINE') {
          const currentJointValue = detail[region]?.[prop];
          const clickedSide = val as string;
          const currentLeft = currentJointValue?.left === true || currentJointValue === 'left';
          const currentRight = currentJointValue?.right === true || currentJointValue === 'right';
          let newLeft = currentLeft;
          let newRight = currentRight;

          if (clickedSide === 'left') {
            newLeft = !currentLeft;
          } else if (clickedSide === 'right') {
            newRight = !currentRight;
          } else if (clickedSide === 'both') {
            newLeft = true;
            newRight = true;
          }

          if (!newLeft && !newRight) {
            processedVal = undefined;
          } else {
            const result: { [key: string]: boolean } = {};
            if (newLeft) result['left'] = true;
            if (newRight) result['right'] = true;
            processedVal = result;
          }
        }

        const newRegionDetail = { ...detail[region] };

        if (processedVal === undefined) {
          delete newRegionDetail[prop];
        } else {
          newRegionDetail[prop] = processedVal;
        }

        // rm entire region if no joints selected..
        // ..but filter out metadata keys (complaintIds, addedManually) when checking for joint selections.
        const hasActualJointSelections = Object.keys(newRegionDetail).some(
          key => key !== 'complaintIds' && key !== 'addedManually'
        );

        const newDetail = { ...detail };

        if (!hasActualJointSelections) {
          delete newDetail[region];
        } else {
          newDetail[region] = newRegionDetail;
        }

        const newVal = {
          ...prev,
          soapCustomEditWarning: {
            ...prev?.soapCustomEditWarning,
            objective: false,
          },
          unifiedObjective: {
            ...prev.unifiedObjective,
            [part]: {
              ...prev.unifiedObjective?.[part],
              detail: newDetail,
            },
          },
        };
        const planWizardCategoryAndTreatmentVisible =
          (prev?.planWizardCategory && findTreatmentCategory(prev?.planWizardCategory)?.visible) ||
          !prev?.planWizardCategory;

        // plan update logic for Joints when SPINE or RIBS regions are affected
        if (
          planWizardCategoryAndTreatmentVisible &&
          part === 'Joints' &&
          ((region === 'RIBS' && typeof prop === 'string' && prop.includes('costovertebral')) ||
            region === 'SPINE')
        ) {
          const copyOfDetail = getCopyOfDetail(newVal?.plan?.treatments?.cmt ?? {});

          const planTypesInDetailNote = Object.keys(copyOfDetail || {})?.reduce(
            (obj, key) => {
              obj[copyOfDetail[key]?.region] = copyOfDetail[key]?.type || [];

              return obj;
            },
            {} as Record<string, string[]>
          );

          // Convert unified objective data to legacy format for the helper functions
          const legacyFormatNote: LegacyFormatNote = {};
          const activeComplaint = complaints?.find(
            complaint =>
              complaint.status !== ComplaintStatuses.INACTIVE &&
              complaint.status !== ComplaintStatuses.PAUSED &&
              !complaint.skipped
          );

          if (activeComplaint?.id) {
            legacyFormatNote[activeComplaint.id] = {
              objective: {
                Joints: {
                  detail: newVal.unifiedObjective?.Joints?.detail || {},
                },
              },
            };
          }

          const spineRegionsAndDetailNotes = getNumberOfSpineRegionsAndDetailNotes(
            legacyFormatNote,
            complaints || [],
            planTypesInDetailNote
          );

          const existsSelection = existsTreatmentSelection(
            prev?.planWizardCategory,
            selection => String(selection.ID) === (spineRegionsAndDetailNotes.keyCount as string)
          );

          if (spineRegionsAndDetailNotes?.keyCount && existsSelection) {
            newVal.plan = {
              ...newVal?.plan,
              treatments: {
                ...newVal?.plan?.treatments,
                cmt: {
                  [spineRegionsAndDetailNotes.keyCount]: {
                    units: 1,
                    services: [cmtServiceCodes?.[spineRegionsAndDetailNotes.keyCount]],
                    unitServices: null,
                    detail: spineRegionsAndDetailNotes?.detailNotes,
                  },
                },
              },
            };
          } else {
            // Create new references so the v2 wrapper's reference comparison
            // detects the change (delete mutates in-place, which is invisible).
            const { cmt: _, ...remainingTreatments } = newVal.plan?.treatments || {};

            if (!Object.keys(remainingTreatments).length) {
              const { treatments: __, ...restPlan } = newVal.plan || {};
              newVal.plan = { ...restPlan };
            } else {
              newVal.plan = {
                ...newVal.plan,
                treatments: remainingTreatments,
              };
            }
          }
        } else if (part === 'Joints' && planWizardCategoryAndTreatmentVisible) {
          // Handle extraspinal joints plan update
          // Convert unified objective data to legacy format for the helper function
          const legacyFormatNote: LegacyFormatNote = {};

          if (complaints) {
            complaints.forEach(complaint => {
              if (
                complaint.status !== ComplaintStatuses.INACTIVE &&
                complaint.status !== ComplaintStatuses.PAUSED &&
                !complaint.skipped
              ) {
                if (complaint.id) {
                  legacyFormatNote[complaint.id] = {
                    objective: {
                      Joints: {
                        detail: newVal.unifiedObjective?.Joints?.detail || {},
                      },
                    },
                  };
                }
              }
            });
          }

          const cmtExtraspinalResult = calculateCmtExtraspinal(legacyFormatNote, complaints || []);

          const existsSelectionExtraspinal = existsTreatmentSelection(
            prev?.planWizardCategory,
            selection => selection.subCategory === treatmentSubCategories.CMT_EXTRASPINAL
          );

          if (existsSelectionExtraspinal && cmtExtraspinalResult) {
            newVal.plan = {
              ...newVal?.plan,
              treatments: {
                ...newVal?.plan?.treatments,
                ...cmtExtraspinalResult,
              },
            };
          } else {
            const { cmtExtraspinal: _, ...remainingTreatments } = newVal.plan?.treatments || {};

            if (!Object.keys(remainingTreatments).length) {
              const { treatments: __, ...restPlan } = newVal.plan || {};
              newVal.plan = { ...restPlan };
            } else {
              newVal.plan = {
                ...newVal.plan,
                treatments: remainingTreatments,
              };
            }
          }
        }

        return newVal;
      });
    },
    [setVisitForm, complaints, signed, readOnly]
  );

  const onChangeImprovement = useCallback(
    (part: ObjectiveParts) => (improvement: ImprovementOptions | null) => {
      if (signed || readOnly) return;
      setVisitForm(prev => {
        const currentUnifiedObjective = prev.unifiedObjective || {};
        const currentPart = currentUnifiedObjective[part] || {};

        return {
          ...prev,
          unifiedObjective: {
            ...currentUnifiedObjective,
            [part]: {
              ...currentPart,
              value: improvement,
            },
          },
          soapCustomEditWarning: {
            ...prev?.soapCustomEditWarning,
            objective: false,
          },
        };
      });
    },
    [setVisitForm, signed, readOnly]
  );

  const onCarryForwardChange = useCallback(
    (carryForward: boolean) => {
      if (readOnly) return;
      setVisitForm(prev => ({
        ...prev,
        unifiedObjective: {
          ...(prev.unifiedObjective || {}),
          carryForward,
        },
      }));
    },
    [setVisitForm, readOnly]
  );

  const onRemoveRegion = useCallback(
    (region: string) => {
      if (signed || isSaving || readOnly) return;
      setVisitForm(prev => {
        const newUnifiedObjective = { ...prev.unifiedObjective };

        if (newUnifiedObjective?.Joints?.detail?.[region]) {
          const newJointsDetail = { ...newUnifiedObjective.Joints.detail };
          delete newJointsDetail[region];

          if (Object.keys(newJointsDetail).length === 0) {
            delete newUnifiedObjective.Joints;
          } else {
            newUnifiedObjective.Joints = {
              ...newUnifiedObjective.Joints,
              detail: newJointsDetail,
            };
          }
        }

        const newVal: Visit = {
          ...prev,
          unifiedObjective: newUnifiedObjective,
        };

        // Recalculate plan treatments after removing a region
        const planWizardCategoryAndTreatmentVisible =
          (prev?.planWizardCategory && findTreatmentCategory(prev?.planWizardCategory)?.visible) ||
          !prev?.planWizardCategory;

        if (planWizardCategoryAndTreatmentVisible) {
          // SPINE and RIBS affect spinal CMT (costovertebral ribs count as spinal)
          if (region === 'SPINE' || region === 'RIBS') {
            const legacyFormatNote: LegacyFormatNote = {};
            const activeComplaint = complaints?.find(
              complaint =>
                complaint.status !== ComplaintStatuses.INACTIVE &&
                complaint.status !== ComplaintStatuses.PAUSED &&
                !complaint.skipped
            );

            if (activeComplaint?.id) {
              legacyFormatNote[activeComplaint.id] = {
                objective: {
                  Joints: {
                    detail: newVal.unifiedObjective?.Joints?.detail || {},
                  },
                },
              };
            }

            const copyOfDetail = getCopyOfDetail(newVal?.plan?.treatments?.cmt ?? {});
            const planTypesInDetailNote = Object.keys(copyOfDetail || {})?.reduce(
              (obj, key) => {
                obj[copyOfDetail[key]?.region] = copyOfDetail[key]?.type || [];
                return obj;
              },
              {} as Record<string, string[]>
            );

            const spineRegionsAndDetailNotes = getNumberOfSpineRegionsAndDetailNotes(
              legacyFormatNote,
              complaints || [],
              planTypesInDetailNote
            );

            const existsSelection = existsTreatmentSelection(
              prev?.planWizardCategory,
              selection => String(selection.ID) === (spineRegionsAndDetailNotes.keyCount as string)
            );

            if (spineRegionsAndDetailNotes?.keyCount && existsSelection) {
              newVal.plan = {
                ...newVal?.plan,
                treatments: {
                  ...newVal?.plan?.treatments,
                  cmt: {
                    [spineRegionsAndDetailNotes.keyCount]: {
                      units: 1,
                      services: [cmtServiceCodes?.[spineRegionsAndDetailNotes.keyCount]],
                      unitServices: null,
                      detail: spineRegionsAndDetailNotes?.detailNotes,
                    },
                  },
                },
              };
            } else {
              const { cmt: _, ...remainingTreatments } = newVal.plan?.treatments || {};

              if (!Object.keys(remainingTreatments).length) {
                const { treatments: __, ...restPlan } = newVal.plan || {};
                newVal.plan = { ...restPlan };
              } else {
                newVal.plan = {
                  ...newVal.plan,
                  treatments: remainingTreatments,
                };
              }
            }
          }

          // Non-SPINE regions affect extraspinal CMT
          if (region !== 'SPINE') {
            const legacyFormatNote: LegacyFormatNote = {};
            if (complaints) {
              complaints.forEach(complaint => {
                if (
                  complaint.status !== ComplaintStatuses.INACTIVE &&
                  complaint.status !== ComplaintStatuses.PAUSED &&
                  !complaint.skipped
                ) {
                  if (complaint.id) {
                    legacyFormatNote[complaint.id] = {
                      objective: {
                        Joints: {
                          detail: newVal.unifiedObjective?.Joints?.detail || {},
                        },
                      },
                    };
                  }
                }
              });
            }

            const cmtExtraspinalResult = calculateCmtExtraspinal(
              legacyFormatNote,
              complaints || []
            );

            const existsSelectionExtraspinal = existsTreatmentSelection(
              prev?.planWizardCategory,
              selection => selection.subCategory === treatmentSubCategories.CMT_EXTRASPINAL
            );

            if (existsSelectionExtraspinal && cmtExtraspinalResult) {
              newVal.plan = {
                ...newVal?.plan,
                treatments: {
                  ...newVal?.plan?.treatments,
                  ...cmtExtraspinalResult,
                },
              };
            } else {
              const { cmtExtraspinal: _, ...remainingTreatments } = newVal.plan?.treatments || {};

              if (!Object.keys(remainingTreatments).length) {
                const { treatments: __, ...restPlan } = newVal.plan || {};
                newVal.plan = { ...restPlan };
              } else {
                newVal.plan = {
                  ...newVal.plan,
                  treatments: remainingTreatments,
                };
              }
            }
          }
        }

        return newVal;
      });
    },
    [signed, isSaving, readOnly, setVisitForm, complaints]
  );

  const onChangeUnifiedDetail = useCallback(
    (part: ObjectiveParts) => (val: Record<string, unknown>) => {
      if (signed || readOnly) return;
      setVisitForm(prev => {
        const currentUnifiedObjective = prev.unifiedObjective || {};
        const currentPart = currentUnifiedObjective[part] || {};

        return {
          ...prev,
          soapCustomEditWarning: {
            ...prev?.soapCustomEditWarning,
            objective: false,
          },
          unifiedObjective: {
            ...currentUnifiedObjective,
            [part]: {
              ...currentPart,
              detail: val,
            },
          },
        };
      });
    },
    [setVisitForm, signed, readOnly]
  );

  const detail = (part: ObjectiveParts) => {
    if (interceptPartOpen?.(part, 'mobile')) return;
    switch (part) {
      case ObjectiveParts.Joints:
        setJointsIsOpen(true);
        break;
      case ObjectiveParts.RangeOfMotion:
        setRangeOfMotionIsOpen(true);
        break;
      case ObjectiveParts.Myofascial:
        setMyofascialIsOpen(true);
        break;
      case ObjectiveParts.Orthopedic:
        setOrthopedicIsOpen(true);
        break;
      case ObjectiveParts.Neurological:
        setNeurologicalIsOpen(true);
        break;
      default:
        break;
    }
  };

  const detailDesktop = (part: ObjectiveParts) => {
    if (interceptPartOpen?.(part, 'desktop')) return;
    switch (part) {
      case ObjectiveParts.Joints:
        setPartOpen(ObjectiveParts.Joints);
        break;
      case ObjectiveParts.RangeOfMotion:
        setPartOpen(ObjectiveParts.RangeOfMotion);
        break;
      case ObjectiveParts.Myofascial:
        setPartOpen(ObjectiveParts.Myofascial);
        break;
      case ObjectiveParts.Orthopedic:
        setPartOpen(ObjectiveParts.Orthopedic);
        break;
      case ObjectiveParts.Neurological:
        setPartOpen(ObjectiveParts.Neurological);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <div className="flex flex-col w-full gap-6 p-3">
        {' '}
        {!visitForm?.signed &&
          visitForm?.soapTemplate !== SOAPTemplates.Blank &&
          visitForm?.soapCustomEditWarning?.objective && <SOAPChangesWarning />}
        <div className="flex flex-row w-full gap-4 min-w-0">
          <div
            className={
              soloPartBelowXl ? 'hidden xl:block xl:w-96 xl:shrink-0' : 'w-full xl:w-96 xl:shrink-0'
            }
          >
            <div className="flex flex-col text-gray-900 bg-white border-b border-gray-300 divide-y divide-gray-300 rounded-lg shadow-sm dark:bg-dark-gray-800 print:ring-0 print:shadow-none ring-1 ring-gray-300 dark:ring-dark-gray-600 dark:divide-dark-gray-600 dark:text-dark-gray-50 dark:border-dark-gray-600">
              <Improvement
                title="Joints"
                detail={() => detail(ObjectiveParts.Joints)}
                isSaving={isSaving}
                detailDesktop={() => detailDesktop(ObjectiveParts.Joints)}
                detailSelected={partOpen === ObjectiveParts.Joints}
                onChange={onChangeImprovement(ObjectiveParts.Joints)}
                value={visitForm.unifiedObjective?.Joints?.value}
                showOptions={!!visitForm.unifiedObjective?.Joints?.showGlobal}
                top
                largeText
                disabled={signed || readOnly}
              />
              <Improvement
                title="Range of Motion"
                detail={() => detail(ObjectiveParts.RangeOfMotion)}
                isSaving={isSaving}
                detailDesktop={() => detailDesktop(ObjectiveParts.RangeOfMotion)}
                detailSelected={partOpen === ObjectiveParts.RangeOfMotion}
                onChange={onChangeImprovement(ObjectiveParts.RangeOfMotion)}
                value={visitForm.unifiedObjective?.RangeOfMotion?.value}
                showOptions={!!visitForm.unifiedObjective?.RangeOfMotion?.showGlobal}
                largeText
                disabled={signed || readOnly}
              />
              <Improvement
                title="Myofascial"
                detail={() => detail(ObjectiveParts.Myofascial)}
                isSaving={isSaving}
                detailDesktop={() => detailDesktop(ObjectiveParts.Myofascial)}
                detailSelected={partOpen === ObjectiveParts.Myofascial}
                onChange={onChangeImprovement(ObjectiveParts.Myofascial)}
                value={visitForm.unifiedObjective?.Myofascial?.value}
                showOptions={!!visitForm.unifiedObjective?.Myofascial?.showGlobal}
                largeText
                disabled={signed || readOnly}
              />
              <Improvement
                title="Orthopedic"
                detail={() => detail(ObjectiveParts.Orthopedic)}
                isSaving={isSaving}
                detailDesktop={() => detailDesktop(ObjectiveParts.Orthopedic)}
                detailSelected={partOpen === ObjectiveParts.Orthopedic}
                onChange={onChangeImprovement(ObjectiveParts.Orthopedic)}
                value={visitForm.unifiedObjective?.Orthopedic?.value}
                showOptions={!!visitForm.unifiedObjective?.Orthopedic?.showGlobal}
                largeText
                disabled={signed || readOnly}
              />
              <Improvement
                title="Neurologic"
                detail={() => detail(ObjectiveParts.Neurological)}
                isSaving={isSaving}
                detailDesktop={() => detailDesktop(ObjectiveParts.Neurological)}
                detailSelected={partOpen === ObjectiveParts.Neurological}
                onChange={onChangeImprovement(ObjectiveParts.Neurological)}
                value={visitForm.unifiedObjective?.Neurological?.value}
                showOptions={!!visitForm.unifiedObjective?.Neurological?.showGlobal}
                bottom
                largeText
                disabled={signed || readOnly}
              />
            </div>
            {desktopAside ? (
              <div className="hidden xl:block mt-4 xl:sticky xl:top-4">{desktopAside}</div>
            ) : null}
          </div>
          {partOpen === ObjectiveParts.Joints ? (
            <div
              className={`${soloPartBelowXl ? 'contents' : 'hidden xl:contents'} w-full min-w-0 bg-white dark:bg-dark-gray-800`}
            >
              <div className="flex flex-col flex-1 min-w-0 w-full mx-auto overflow-hidden text-gray-900 bg-white border-b border-gray-300 divide-y divide-gray-300 rounded-lg shadow-sm dark:bg-dark-gray-800 print:ring-0 print:shadow-none ring-1 ring-gray-300 dark:ring-dark-gray-600 dark:divide-dark-gray-600 dark:text-dark-gray-50 dark:border-dark-gray-600">
                <Joints
                  value={visitForm.unifiedObjective?.Joints?.detail || []}
                  onChange={onChangeDetail(ObjectiveParts.Joints)}
                  onRemoveRegion={onRemoveRegion}
                  hideRegionControls={hideRegionControls}
                  interceptRegionSwitch={
                    interceptRegionSwitch && (() => interceptRegionSwitch(ObjectiveParts.Joints))
                  }
                  isFetching={isFetching}
                  signed={signed || readOnly}
                />
              </div>
            </div>
          ) : (
            <div
              className={`${soloPartBelowXl ? 'contents' : 'hidden xl:contents'} w-full min-w-0 bg-white dark:bg-dark-gray-800`}
            >
              <div className="flex flex-col w-full mx-auto text-gray-900 bg-white border-b border-gray-300 divide-y divide-gray-300 rounded-lg shadow-sm dark:bg-dark-gray-800 print:ring-0 print:shadow-none ring-1 ring-gray-300 dark:ring-dark-gray-600 dark:divide-dark-gray-600 dark:text-dark-gray-50 dark:border-dark-gray-600">
                {partOpen === ObjectiveParts.RangeOfMotion && (
                  <div className="p-4">
                    <UnifiedRangeOfMotion
                      value={visitForm.unifiedObjective?.RangeOfMotion?.detail}
                      onChange={onChangeUnifiedDetail(ObjectiveParts.RangeOfMotion)}
                      romSliderStep={romSliderStep}
                      signed={signed || !!readOnly}
                      hideRemoveRegion={hideRegionControls}
                      interceptRegionSwitch={
                        interceptRegionSwitch &&
                        (() => interceptRegionSwitch(ObjectiveParts.RangeOfMotion))
                      }
                    />
                  </div>
                )}
                {partOpen === ObjectiveParts.Myofascial && (
                  <UnifiedMyofascial
                    onAddMusclesIntercept={interceptAddMuscles}
                    value={visitForm.unifiedObjective?.Myofascial?.detail}
                    onChange={onChangeUnifiedDetail(ObjectiveParts.Myofascial)}
                    conditions={filteredActiveComplaintConditions}
                    signed={signed || !!readOnly}
                  />
                )}
                {partOpen === ObjectiveParts.Neurological && (
                  <UnifiedNeurological
                    value={visitForm.unifiedObjective?.Neurological?.detail}
                    onChange={onChangeUnifiedDetail(ObjectiveParts.Neurological)}
                    signed={signed || !!readOnly}
                  />
                )}
                {partOpen === ObjectiveParts.Orthopedic && (
                  <UnifiedOrthopedic
                    onDetailsIntercept={interceptOrthoDetails}
                    onAddTestsIntercept={interceptAddTests}
                    value={visitForm.unifiedObjective?.Orthopedic?.detail}
                    onChange={onChangeUnifiedDetail(ObjectiveParts.Orthopedic)}
                    evalsThatWeNeed={filteredOrthopedicTests}
                    evaluations={allEvaluations?.data || []}
                    signed={signed || !!readOnly}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <div>
          <Textarea
            rows={5}
            macros={me.macros}
            disabled={signed || isSaving || readOnly}
            value={localTextareaValue}
            onChange={val => {
              if (signed || isSaving || readOnly) return;
              editLocalNote(val);
            }}
            onBlur={() => collaborative?.flushPendingWrites()}
            label={
              <div className="flex justify-between items-center w-full pb-2">
                <span>Notes</span>
                {!hideCarryForward && (
                  <Checkbox
                    name="carry-forward-objective-unified"
                    value={!!visitForm.unifiedObjective?.carryForward}
                    onChange={onCarryForwardChange}
                    disabled={signed || readOnly || isSaving}
                    label="Carry Forward"
                  />
                )}
              </div>
            }
            className="pb-64"
          />
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={rangeOfMotionIsOpen}
        close={() => setRangeOfMotionIsOpen(false)}
        isFullScreen={true}
      >
        <div className="pt-8">
          <UnifiedRangeOfMotion
            close={() => setRangeOfMotionIsOpen(false)}
            value={visitForm.unifiedObjective?.RangeOfMotion?.detail}
            onChange={onChangeUnifiedDetail(ObjectiveParts.RangeOfMotion)}
            romSliderStep={romSliderStep}
            signed={signed || !!readOnly}
            hideRemoveRegion={hideRegionControls}
            interceptRegionSwitch={
              interceptRegionSwitch && (() => interceptRegionSwitch(ObjectiveParts.RangeOfMotion))
            }
          />
        </div>
      </Modal>

      <Modal isOpen={jointsIsOpen} close={() => setJointsIsOpen(false)} isFullScreen={true}>
        <div className="pt-8">
          <Joints
            value={visitForm.unifiedObjective?.Joints?.detail || {}}
            close={() => setJointsIsOpen(false)}
            onChange={onChangeDetail(ObjectiveParts.Joints)}
            onRemoveRegion={onRemoveRegion}
            hideRegionControls={hideRegionControls}
            interceptRegionSwitch={
              interceptRegionSwitch && (() => interceptRegionSwitch(ObjectiveParts.Joints))
            }
            isFetching={false}
            signed={signed || !!readOnly}
          />
        </div>
      </Modal>

      <Modal isOpen={myofascialIsOpen} close={() => setMyofascialIsOpen(false)} isFullScreen={true}>
        <div className="pt-8">
          <UnifiedMyofascial
            onAddMusclesIntercept={interceptAddMuscles}
            close={() => setMyofascialIsOpen(false)}
            value={visitForm.unifiedObjective?.Myofascial?.detail}
            onChange={onChangeUnifiedDetail(ObjectiveParts.Myofascial)}
            conditions={filteredActiveComplaintConditions}
            signed={signed || !!readOnly}
          />
        </div>
      </Modal>

      <Modal
        isOpen={neurologicalIsOpen}
        isFullScreen={true}
        close={() => setNeurologicalIsOpen(false)}
      >
        <div className="pt-8">
          <UnifiedNeurological
            close={() => setNeurologicalIsOpen(false)}
            value={visitForm.unifiedObjective?.Neurological?.detail || {}}
            onChange={onChangeUnifiedDetail(ObjectiveParts.Neurological)}
            signed={signed || !!readOnly}
          />
        </div>
      </Modal>

      <Modal isOpen={orthopedicIsOpen} close={() => setOrthopedicIsOpen(false)} isFullScreen={true}>
        <div className="pt-8">
          <UnifiedOrthopedic
            onDetailsIntercept={interceptOrthoDetails}
            onAddTestsIntercept={interceptAddTests}
            close={() => setOrthopedicIsOpen(false)}
            value={visitForm.unifiedObjective?.Orthopedic?.detail}
            onChange={onChangeUnifiedDetail(ObjectiveParts.Orthopedic)}
            evalsThatWeNeed={filteredOrthopedicTests}
            evaluations={allEvaluations?.data || []}
            signed={signed || !!readOnly}
          />
        </div>
      </Modal>
    </>
  );
};

export default UnifiedObjectiveStep;
